"use client";

import { useState } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { motion } from "motion/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { toast } from "react-toastify";
import Logo from "@/components/ui/Logo/Logo";
import ProgressButton from "@/components/ui/Button/ProgressButton";

export default function TeamsDemoPage() {
    const params = useParams();
    const locale = params.locale;
    const t = useTranslations("teams.demo");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        jobTitle: "",
        teamSize: "",
        learningNeeds: "",
    });

    const [errors, setErrors] = useState({});

    const teamSizeOptions = [
        { value: "1-10", label: "1-10" },
        { value: "11-50", label: "11-50" },
        { value: "51-200", label: "51-200" },
        { value: "201-500", label: "201-500" },
        { value: "501-1000", label: "501-1000" },
        { value: "1000+", label: "1000+" },
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = t("firstNameRequired");
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = t("lastNameRequired");
        }

        if (!formData.company.trim()) {
            newErrors.company = t("companyRequired");
        }

        if (!formData.email.trim()) {
            newErrors.email = t("emailRequired");
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t("emailInvalid");
        }

        if (!formData.jobTitle.trim()) {
            newErrors.jobTitle = t("jobTitleRequired");
        }

        if (!formData.teamSize) {
            newErrors.teamSize = t("teamSizeRequired");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const response = await fetch("/api/teams/demo-request", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                toast.error(data.message || t("errorGeneric"));
                setLoading(false);
                return;
            }

            setSubmitted(true);
        } catch (error) {
            console.error("Error:", error);
            toast.error(t("errorGeneric"));
            setLoading(false);
        }
    };

    const stats = [
        {
            value: `${process.env.NEXT_PUBLIC_NUMBER_OF_TEAMS || "500"}+`,
            label: t("statCompanies"),
            icon: "users-group-rounded-linear",
        },
        { value: "10x", label: t("statCompletion"), icon: "graph-up-linear" },
        {
            value: `${process.env.TRUSTPILOT_NOTE || "4.9"}/5`,
            label: t("statRating"),
            icon: "medal-ribbon-star-linear",
        },
    ];

    const benefits = [
        {
            icon: "square-academic-cap-linear",
            title: t("benefit1Title"),
            description: t("benefit1Desc"),
        },
        {
            icon: "chart-2-linear",
            title: t("benefit2Title"),
            description: t("benefit2Desc"),
        },
        {
            icon: "bolt-linear",
            title: t("benefit3Title"),
            description: t("benefit3Desc"),
        },
        {
            icon: "medal-ribbon-star-linear",
            title: t("benefit4Title"),
            description: t("benefit4Desc"),
        },
    ];

    const trustedLogos = [
        { src: "/static/companies/google.webp", alt: "Google" },
        { src: "/static/companies/capgemini.webp", alt: "Capgemini" },
        { src: "/static/companies/deloitte.webp", alt: "Deloitte" },
        { src: "/static/companies/amazon.webp", alt: "Amazon" },
        { src: "/static/companies/ibm.webp", alt: "IBM" },
    ];

    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-grayDark-950 dark:to-grayDark-900 flex items-center justify-center px-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center max-w-lg"
                >
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                            delay: 0.2,
                            type: "spring",
                            stiffness: 200,
                        }}
                        className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-green-500/25"
                    >
                        <SolarIcon name="check-circle-linear" className="w-10 h-10 text-white" />
                    </motion.div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                        {t("successTitle")}
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-grayDark-400 mb-8">
                        {t("successMessage")}
                    </p>

                    <div className="bg-white dark:bg-grayDark-800 rounded-2xl p-6 border border-gray-100 dark:border-grayDark-700 shadow-sm mb-8">
                        <div className="flex items-center gap-3 text-left">
                            <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                <SolarIcon name="calendar-linear" className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <p className="font-medium text-gray-900 dark:text-white mb-0">
                                    {t("successNextStep")}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                    {t("successNextStepDesc")}
                                </p>
                            </div>
                        </div>
                    </div>

                    <Link
                        href={`/${locale}/teams`}
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-grayDark-900 rounded-xl font-medium hover:bg-gray-800 dark:hover:bg-grayDark-100 transition-all hover:shadow-lg"
                    >
                        {t("backToTeams")}
                        <SolarIcon name="arrow-right-linear" className="w-4 h-4" />
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary/5 dark:from-grayDark-950 dark:via-grayDark-900 dark:to-primary/10">
            {/* Header */}
            <header className="bg-white dark:bg-grayDark-900 border-b border-gray-100 dark:border-grayDark-800">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link href={`/${locale}`}>
                            <Logo className="h-8" />
                        </Link>
                        <div className="h-6 w-px bg-gray-200 dark:bg-grayDark-700" />
                        <Link
                            href={`/${locale}/teams`}
                            className="text-primary font-semibold text-sm flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                        >
                            <SolarIcon name="users-group-rounded-linear" className="w-4 h-4" />
                            Teams
                        </Link>
                    </div>
                    <Link
                        href={`/${locale}/teams/signup`}
                        className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-grayDark-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                        {t("alreadyConvinced")}
                        <SolarIcon name="arrow-right-linear" className="w-4 h-4" />
                    </Link>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-8 md:py-16">
                <div className="grid xl:grid-cols-2 gap-8 xl:gap-20 items-start">
                    {/* Left: Content - Hidden on mobile, shown on xl+ */}
                    <div className="hidden xl:block xl:sticky xl:top-32">
                        {/* Badge */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 dark:bg-primary/20 rounded-full mb-6"
                        >
                            <SolarIcon name="stars-linear" className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium text-primary">
                                {t("badge")}
                            </span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white tracking-tight mb-6"
                        >
                            {t("title")}
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-xl text-gray-600 dark:text-grayDark-400 mb-10 leading-relaxed"
                        >
                            {t("subtitle")}
                        </motion.p>

                        {/* Stats */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="grid grid-cols-3 gap-4 mb-10"
                        >
                            {stats.map((stat, index) => (
                                <div
                                    key={index}
                                    className="bg-white dark:bg-grayDark-800 rounded-2xl p-4 border border-gray-100 dark:border-grayDark-700 shadow-sm"
                                >
                                    <SolarIcon name={stat.icon} className="w-5 h-5 text-primary mb-2" />
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {stat.value}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-grayDark-400">
                                        {stat.label}
                                    </div>
                                </div>
                            ))}
                        </motion.div>

                        {/* Benefits Grid */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="grid grid-cols-2 gap-4 mb-10"
                        >
                            {benefits.map((benefit, index) => (
                                <div
                                    key={index}
                                    className="flex items-start gap-3 p-4 bg-white dark:bg-grayDark-800 rounded-xl border border-gray-100 dark:border-grayDark-700"
                                >
                                    <div className="w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <SolarIcon name={benefit.icon} className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-0.5">
                                            {benefit.title}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-grayDark-400 leading-relaxed mb-0">
                                            {benefit.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </motion.div>

                        {/* Trusted by */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="mt-10"
                        >
                            <p className="text-xs text-gray-400 dark:text-grayDark-500 uppercase tracking-wider mb-4">
                                {t("trustedBy")}
                            </p>
                            <div className="flex flex-wrap items-center gap-6">
                                {trustedLogos.map((logo, index) => (
                                    <img
                                        key={index}
                                        src={logo.src}
                                        alt={logo.alt}
                                        className="h-6 w-auto brightness-0 dark:brightness-100 opacity-40 hover:opacity-60 transition-all"
                                    />
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                        className="xl:col-start-2 max-w-xl mx-auto xl:max-w-none"
                    >
                        <form
                            onSubmit={handleSubmit}
                            className="xl:bg-white xl:dark:bg-grayDark-800 xl:rounded-3xl xl:p-10 xl:shadow-2xl xl:shadow-gray-200/50 xl:dark:shadow-black/20 xl:border xl:border-gray-100 xl:dark:border-grayDark-700"
                        >
                            {/* Mobile header - shown only on mobile/tablet */}
                            <div className="xl:hidden mb-6">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 dark:bg-primary/20 rounded-full mb-4">
                                    <SolarIcon name="stars-linear" className="w-3.5 h-3.5 text-primary" />
                                    <span className="text-xs font-medium text-primary">
                                        {t("badge")}
                                    </span>
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight mb-2">
                                    {t("title")}
                                </h1>
                                <p className="text-sm text-gray-600 dark:text-grayDark-400">
                                    {t("subtitle")}
                                </p>
                            </div>

                            <div className="hidden xl:block text-center mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    {t("formTitle")}
                                </h2>
                                <p className="text-gray-500 dark:text-grayDark-400">
                                    {t("formSubtitle")}
                                </p>
                            </div>

                            <div className="space-y-5 sm:space-y-6">
                                {/* First name / Last name */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-2">
                                            {t("firstName")}{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            placeholder="Jean"
                                            className={`w-full px-4 py-3 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-grayDark-500 focus:outline-hidden focus:border-primary transition-all ${
                                                errors.firstName
                                                    ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-500/50"
                                                    : "border-gray-100 dark:border-grayDark-600 bg-gray-50 dark:bg-grayDark-700 focus:bg-white dark:focus:bg-grayDark-600"
                                            }`}
                                            disabled={loading}
                                        />
                                        {errors.firstName && (
                                            <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                                                <span className="w-1 h-1 bg-red-500 rounded-full" />
                                                {errors.firstName}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-2">
                                            {t("lastName")}{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            placeholder="Dupont"
                                            className={`w-full px-4 py-3 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-grayDark-500 focus:outline-hidden focus:border-primary transition-all ${
                                                errors.lastName
                                                    ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-500/50"
                                                    : "border-gray-100 dark:border-grayDark-600 bg-gray-50 dark:bg-grayDark-700 focus:bg-white dark:focus:bg-grayDark-600"
                                            }`}
                                            disabled={loading}
                                        />
                                        {errors.lastName && (
                                            <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                                                <span className="w-1 h-1 bg-red-500 rounded-full" />
                                                {errors.lastName}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Company */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-2">
                                        {t("company")}{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        placeholder="Acme Corporation"
                                        className={`w-full px-4 py-3 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-grayDark-500 focus:outline-hidden focus:border-primary transition-all ${
                                            errors.company
                                                ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-500/50"
                                                : "border-gray-100 dark:border-grayDark-600 bg-gray-50 dark:bg-grayDark-700 focus:bg-white dark:focus:bg-grayDark-600"
                                        }`}
                                        disabled={loading}
                                    />
                                    {errors.company && (
                                        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                                            <span className="w-1 h-1 bg-red-500 rounded-full" />
                                            {errors.company}
                                        </p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-2">
                                        {t("email")}{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="jean.dupont@acme.com"
                                        className={`w-full px-4 py-3 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-grayDark-500 focus:outline-hidden focus:border-primary transition-all ${
                                            errors.email
                                                ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-500/50"
                                                : "border-gray-100 dark:border-grayDark-600 bg-gray-50 dark:bg-grayDark-700 focus:bg-white dark:focus:bg-grayDark-600"
                                        }`}
                                        disabled={loading}
                                    />
                                    {errors.email && (
                                        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                                            <span className="w-1 h-1 bg-red-500 rounded-full" />
                                            {errors.email}
                                        </p>
                                    )}
                                </div>

                                {/* Job title / Team size */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-2">
                                            {t("jobTitle")}{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            name="jobTitle"
                                            value={formData.jobTitle}
                                            onChange={handleInputChange}
                                            placeholder="CTO"
                                            className={`w-full px-4 py-3 border-2 rounded-xl text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-grayDark-500 focus:outline-hidden focus:border-primary transition-all ${
                                                errors.jobTitle
                                                    ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-500/50"
                                                    : "border-gray-100 dark:border-grayDark-600 bg-gray-50 dark:bg-grayDark-700 focus:bg-white dark:focus:bg-grayDark-600"
                                            }`}
                                            disabled={loading}
                                        />
                                        {errors.jobTitle && (
                                            <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                                                <span className="w-1 h-1 bg-red-500 rounded-full" />
                                                {errors.jobTitle}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-2">
                                            {t("teamSize")}{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <select
                                                name="teamSize"
                                                value={formData.teamSize}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border-2 rounded-xl text-gray-900 dark:text-white bg-gray-50 dark:bg-grayDark-700 focus:outline-hidden focus:border-primary focus:bg-white dark:focus:bg-grayDark-600 transition-all appearance-none cursor-pointer ${
                                                    errors.teamSize
                                                        ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-500/50"
                                                        : "border-gray-100 dark:border-grayDark-600"
                                                } ${
                                                    !formData.teamSize
                                                        ? "text-gray-300 dark:text-grayDark-500"
                                                        : ""
                                                }`}
                                                disabled={loading}
                                            >
                                                <option value="">
                                                    {t("selectTeamSize")}
                                                </option>
                                                {teamSizeOptions.map(
                                                    (option) => (
                                                        <option
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </option>
                                                    ),
                                                )}
                                            </select>
                                            <SolarIcon name="alt-arrow-down-linear" className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                        </div>
                                        {errors.teamSize && (
                                            <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                                                <span className="w-1 h-1 bg-red-500 rounded-full" />
                                                {errors.teamSize}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Learning needs */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-2">
                                        {t("learningNeeds")}
                                        <span className="text-gray-400 dark:text-grayDark-500 font-normal ml-1">
                                            ({t("optional")})
                                        </span>
                                    </label>
                                    <textarea
                                        name="learningNeeds"
                                        value={formData.learningNeeds}
                                        onChange={handleInputChange}
                                        rows={3}
                                        placeholder={t(
                                            "learningNeedsPlaceholder",
                                        )}
                                        className="w-full px-4 py-3 border-2 border-gray-100 dark:border-grayDark-600 bg-gray-50 dark:bg-grayDark-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-grayDark-500 focus:outline-hidden focus:border-primary focus:bg-white dark:focus:bg-grayDark-600 transition-all resize-none"
                                        disabled={loading}
                                    />
                                </div>

                                {/* Submit */}
                                <ProgressButton
                                    type="submit"
                                    disabled={loading}
                                    loading={loading}
                                    overlayClassName="w-full py-4 font-semibold flex items-center justify-center gap-2"
                                    className="w-full btn-3d lip-orange bg-primary text-white py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
                                >
                                    {t("submitButton")}
                                    <SolarIcon name="arrow-right-linear" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </ProgressButton>

                                {/* Benefits reminder */}
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 pt-2">
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-grayDark-400">
                                        <SolarIcon name="check-circle-linear" className="w-3.5 h-3.5 text-green-500" />
                                        {t("freeDemo")}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-grayDark-400">
                                        <SolarIcon name="check-circle-linear" className="w-3.5 h-3.5 text-green-500" />
                                        {t("noCommitment")}
                                    </div>
                                </div>

                                {/* Mobile/Tablet stats */}
                                <div className="xl:hidden grid grid-cols-3 gap-2 pt-4 border-t border-gray-100 dark:border-grayDark-700">
                                    {stats.map((stat, index) => (
                                        <div
                                            key={index}
                                            className="text-center"
                                        >
                                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                                                {stat.value}
                                            </div>
                                            <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                                {stat.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Legal */}
                                <p className="text-xs text-gray-400 dark:text-grayDark-500 text-center leading-relaxed">
                                    {t("legalText")}{" "}
                                    <Link
                                        href={`/${locale}/cgu`}
                                        className="underline hover:text-gray-600 dark:hover:text-grayDark-300 transition-colors"
                                    >
                                        {t("termsOfService")}
                                    </Link>{" "}
                                    {t("and")}{" "}
                                    <Link
                                        href={`/${locale}/privacy`}
                                        className="underline hover:text-gray-600 dark:hover:text-grayDark-300 transition-colors"
                                    >
                                        {t("privacyPolicy")}
                                    </Link>
                                    .
                                </p>
                            </div>
                        </form>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
